import { useState, useRef } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import bookingChickensImg from "@/assets/booking-firepit.png";

const WHATSAPP_NUMBER = "27839481845"; // 0839481845 in international format

export default function Contact() {
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error"
  >("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const sectionRef = useScrollReveal();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const get = (n: string) =>
      ((form.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value ?? "").trim();

    const lines = [
      "*New Booking Enquiry — Rietkuil Farm House*",
      "",
      `*Name:* ${get("name")}`,
      `*Email:* ${get("email")}`,
      `*Phone:* ${get("phone") || "—"}`,
      `*Check-in:* ${get("checkin")}`,
      `*Check-out:* ${get("checkout")}`,
      `*Guests:* ${get("guests") || "—"}`,
      `*Pets:* ${get("pets") || "No"}`,
      "",
      `*Message:*`,
      get("message") || "—",
    ];
    const text = encodeURIComponent(lines.join("\n"));
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;

    setStatus("sending");
    window.open(url, "_blank", "noopener,noreferrer");
    setStatus("success");
    formRef.current?.reset();
    setCharCount(0);
  };


  const inputClass = "w-full px-0 py-3 border-b focus:outline-none text-sm font-sans font-light bg-transparent transition-colors placeholder:font-light";

  return (
    <section id="booking" ref={sectionRef} className="overflow-hidden" style={{ backgroundColor: "#F0EBE0" }}>

      {/* Two-column layout: Chicken photo | Enquiry form */}
      <div className="flex flex-col lg:flex-row">

        {/* Left: Chicken photo */}
        <div className="img-reveal lg:w-[50%] overflow-hidden" style={{ minHeight: "560px" }}>
          <img
            alt="Fire pit and bench at Rietkuil farm at sunset"
            className="w-full h-full object-cover object-center img-washed"
            src={bookingChickensImg}
          />
        </div>

        {/* Right: Send an Enquiry */}
        <div className="lg:w-[50%] flex flex-col justify-center px-12 lg:px-20 py-20 lg:py-24" style={{ backgroundColor: "#F0EBE0" }}>
          <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-6" style={{ color: "#4E4228" }}>Booking Enquiry</p>
          <h2 className="fade-in delay-100 font-serif font-light leading-tight mb-6" style={{ fontSize: "clamp(2rem, 3vw, 3rem)", color: "#3C3530" }}>
            Send an Enquiry
          </h2>
          <div className="fade-in delay-200 w-7 h-px mb-8" style={{ backgroundColor: "#A8B09E" }}></div>
          <p className="fade-in delay-300 font-sans font-light leading-loose text-sm mb-8" style={{ color: "#8C8478" }}>
            We'll confirm availability and get back to you within 24 hours.
          </p>

          {status === "success" ? (
            <div className="flex flex-col items-center text-center">
              <div className="w-11 h-11 flex items-center justify-center mb-6" style={{ backgroundColor: "#D8DCD4" }}>
                <i className="ri-check-line text-xl" style={{ color: "#5E6A54" }}></i>
              </div>
              <h3 className="font-serif font-light text-2xl mb-3" style={{ color: "#3C3530" }}>Enquiry Sent</h3>
              <p className="font-sans font-light text-sm leading-loose" style={{ color: "#8C8478" }}>
                Thanks for reaching out — we'll be in touch within 24 hours.
              </p>
              <button onClick={() => setStatus("idle")}
                className="mt-8 px-10 py-3.5 text-sm tracking-[0.1em] uppercase font-sans font-medium transition-all cursor-pointer whitespace-nowrap rounded-full"
                style={{ backgroundColor: "#4E4228", color: "#F5F0E8" }}>
                Send Another
              </button>
            </div>
          ) : (
            <form id="booking-enquiry-form" data-readdy-form="true" ref={formRef} onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Full Name</label>
                <input required name="name" placeholder="Your name"
                  className={inputClass}
                  style={{ borderColor: "#C8C0B4", color: "#3C3530" }} />
              </div>
              <div>
                <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Email Address</label>
                <input required type="email" name="email" placeholder="your@email.com"
                  className={inputClass}
                  style={{ borderColor: "#C8C0B4", color: "#3C3530" }} />
              </div>
              <div>
                <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Phone Number</label>
                <input type="tel" name="phone" placeholder="+27 ..."
                  className={inputClass}
                  style={{ borderColor: "#C8C0B4", color: "#3C3530" }} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Check-in</label>
                  <input required type="date" name="checkin"
                    className={inputClass}
                    style={{ borderColor: "#C8C0B4", color: "#3C3530" }} />
                </div>
                <div>
                  <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Check-out</label>
                  <input required type="date" name="checkout"
                    className={inputClass}
                    style={{ borderColor: "#C8C0B4", color: "#3C3530" }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Guests</label>
                  <input type="number" name="guests" min={1} max={10} placeholder="e.g. 6"
                    className={inputClass}
                    style={{ borderColor: "#C8C0B4", color: "#3C3530" }} />
                </div>
                <div>
                  <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Pets?</label>
                  <select name="pets" className={inputClass} style={{ borderColor: "#C8C0B4", color: "#3C3530" }}>
                    <option value="No">No pets</option>
                    <option value="Yes">Yes, bringing pets</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-sans font-medium uppercase tracking-[0.1em] mb-1" style={{ color: "#7E7265" }}>Message</label>
                <textarea name="message" maxLength={500} rows={3}
                  placeholder="Any special requests..."
                  className={`${inputClass} resize-none`}
                  style={{ borderColor: "#C8C0B4", color: "#3C3530" }}
                  onChange={(e) => setCharCount(e.target.value.length)}
                ></textarea>
                <p className="text-xs font-sans font-light mt-1 text-right" style={{ color: "#D8D0C4" }}>{charCount}/500</p>
              </div>
              {status === "error" && <p className="text-sm text-center font-sans font-light" style={{ color: "#9C7060" }}>Something went wrong. Please try again.</p>}
              <div className="text-center pt-2">
                <button type="submit" disabled={status === "sending"}
                  className="px-12 py-3.5 text-sm tracking-[0.1em] uppercase font-sans font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer rounded-full"
                  style={{ backgroundColor: "#4E4228", color: "#F5F0E8" }}>
                  {status === "sending" ? "Sending..." : "Send Enquiry"}
                </button>
              </div>
            </form>
          )}
        </div>

      </div>

    </section>
  );
}